import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api.js";
import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";

export default function AdminDevicesPage() {
  const { t } = useT();
  const toast = useToast();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | online | offline
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api("/devices");
      setDevices(res.devices || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return devices.filter((d) => {
      if (filter === "online" && !d.online) return false;
      if (filter === "offline" && d.online) return false;
      if (!s) return true;
      return (
        d.device_name?.toLowerCase().includes(s) ||
        d.hostname?.toLowerCase().includes(s) ||
        d.os?.toLowerCase().includes(s) ||
        d.owner_name?.toLowerCase().includes(s) ||
        d.device_id?.toLowerCase().includes(s)
      );
    });
  }, [devices, search, filter]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/devices/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="eyebrow">Fleet</div>
          <h1 className="h1">Alle Geräte</h1>
        </div>
        <div className="ph-meta">
          {devices.filter((d) => d.online).length} / {devices.length} online
        </div>
      </div>

      <div className="toolbar" style={{ flexWrap: "wrap" }}>
        <div className="search" style={{ flex: 1, minWidth: 240 }}>
          <span className="search-icon">⌕</span>
          <input
            type="text"
            placeholder="Suche nach Name, Owner, OS, UUID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {["all", "online", "offline"].map((f) => (
            <button
              key={f}
              className={`btn ghost ${filter === f ? "primary" : ""}`}
              onClick={() => setFilter(f)}
              style={{ padding: "8px 14px", fontSize: 11 }}
            >
              {f === "all" ? "Alle" : f === "online" ? "Online" : "Offline"}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-line" style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div className="loading">Lade Geräte…</div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          <div className="empty-title">Keine Geräte gefunden</div>
          <div className="empty-body">
            {search ? "Versuche eine andere Suche." : "Noch keine Geräte registriert."}
          </div>
        </div>
      ) : (
        <div style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          overflow: "hidden",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--line)" }}>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Gerät</th>
                <th style={thStyle}>Owner</th>
                <th style={thStyle}>OS</th>
                <th style={thStyle}>Hostname</th>
                <th style={thStyle}>Last Seen</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} style={{ borderBottom: "1px solid var(--line)" }}>
                  <td style={tdStyle}>
                    <span className={`chip ${d.online ? "ok" : ""}`}>
                      <span className={`dot ${d.online ? "live" : "dead"}`} />
                      {d.online ? "online" : "offline"}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{d.device_name || "(unnamed)"}</div>
                    <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)" }}>
                      {d.device_id?.slice(0, 12)}…
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {d.owner_name || <span style={{ color: "var(--text-faint)" }}>—</span>}
                  </td>
                  <td style={tdStyle}>
                    <span className="mono" style={{ fontSize: 11 }}>{d.os || "—"}</span>
                  </td>
                  <td style={tdStyle}>
                    <span className="mono" style={{ fontSize: 11 }}>{d.hostname || "—"}</span>
                  </td>
                  <td style={tdStyle}>
                    <span className="mono" style={{ fontSize: 11, color: "var(--text-dim)" }}>
                      {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "nie"}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <button
                      className="btn ghost danger"
                      style={{ padding: "5px 10px", fontSize: 11 }}
                      onClick={() => setDeleteTarget(d)}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={overlayStyle}
            onClick={() => !busy && setDeleteTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={modalStyle}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="eyebrow" style={{ color: "var(--err)" }}>Achtung</div>
              <h2 style={{ fontSize: 20, margin: "8px 0 12px" }}>Gerät löschen?</h2>
              <p style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 18 }}>
                <strong style={{ color: "var(--text)" }}>{deleteTarget.device_name}</strong> wird unwiderruflich gelöscht.
                Alle Sessions, Zuweisungen und Browser-Access-Daten dieses Geräts werden ebenfalls entfernt.
              </p>
              <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 18 }}>
                Owner: {deleteTarget.owner_name || "—"} <br />
                UUID: {deleteTarget.device_id}
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn ghost" onClick={() => setDeleteTarget(null)} disabled={busy}>
                  Abbrechen
                </button>
                <button className="btn danger" onClick={confirmDelete} disabled={busy}>
                  {busy ? "Lösche…" : "Endgültig löschen"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "12px 16px",
  fontFamily: "var(--mono)",
  fontSize: 10,
  color: "var(--text-dim)",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  fontWeight: 500,
};
const tdStyle = {
  padding: "12px 16px",
  fontSize: 13,
};
const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.7)",
  backdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
  padding: 20,
};
const modalStyle = {
  background: "var(--bg-elev)",
  border: "1px solid var(--err)",
  borderRadius: "var(--r-lg)",
  padding: 32,
  maxWidth: 460,
  width: "100%",
  boxShadow: "0 32px 80px rgba(0,0,0,0.8)",
};
